<template>
  <div class="order-grid col-sm-12 col-md-12 col-lg-12 eh-ds">
    <div v-if="!isMobile()" class="medications-header">
      <h1 class="medications-title">
        Medications
        <help-icon help-id="medications" title="Help for Medications" />
      </h1>
      <div class="medications-nav">
        <span class="nav-link active">Current</span>
        <span class="nav-separator">|</span>
        <router-link to="/inactiveMedications" class="nav-link">Inactive</router-link>
        <span class="nav-separator">|</span>
        <router-link to="/medAdministration" class="nav-link">Administration</router-link>
        <span class="nav-separator">|</span>
        <router-link to="/prePourMedication" class="nav-link">Prepour</router-link>
      </div>
      <div class="medications-actions">
        <button class="add-manually-btn" @click="showAddMed">Add Medication</button>
      </div>
    </div>
    <template v-else>
      <div class="header mobile">MEDICATIONS</div>
      <div class="nav-links mobile">
        <span id="current-link" class="mobile active">Current</span>
        <router-link to="/inactiveMedications" class="mobile">Inactive</router-link>
        <router-link to="/medAdministration">Administration</router-link>
        <router-link to="/prePourMedication" class="mobile">Prepour</router-link>
      </div>
      <div class="medications-actions mobile">
        <button class="add-manually-btn" @click="showAddMed">Add Medication</button>
      </div>
    </template>
    <!-- Add form: manual add or import-to-order -->
    <AddMedicationForm
      :show="showAddMedForm"
      :pastProvloaded="pastProvloaded"
      :pastProvar="pastProvar"
      :isEditMedication="false"
      :isAddNewMed="true"
      :ifStatusIsChange="false"
      :editFormdata="orderPrefillData"
      :modal-title="isOrderingFromImport ? 'Order Medication' : undefined"
      :isSaving="isSavingMedication"
      @close="handleCloseMedForm"
      @save="handleSaveMedication"
      @loadprov="loadPastProvider"
    />
    <!-- Edit form: ordered meds use MedAdmin-style edit with change-order / hold / discontinue -->
    <AddMedicationForm
      :show="showEditMedForm"
      :pastProvloaded="pastProvloaded"
      :pastProvar="pastProvar"
      :isEditMedication="true"
      :isAddNewMed="false"
      :ifStatusIsChange="ifStatusIsChange"
      :editFormdata="editFormdata"
      :isSaving="isSavingMedication"
      :holdSelectorCancelled="holdSelectorCancelledCounter"
      @close="handleCloseEditMedForm"
      @save="handleEditMedicationSave"
      @loadprov="loadPastProvider"
      @freqchange="onEditFreqChange"
      @dosagechange="onEditDosageChange"
      @holddtchange="onEditHoldDateChange"
      @passMedStatus="triggerHoldDCModal"
      @cancelMedHolds="clearMedicationHold"
    />
    <!-- Hold / discontinue time selector for ordered med edits -->
    <div v-if="showHoldSelector" class="modal-overlay">
      <div class="modal-content">
        <HoldTimeSelector
          v-if="selectedMedicationForHold"
          :statusOption="selectedStatusOption"
          :medication-times="holdTimes"
          :timepairs="selectedMedicationForHold.time_status_pairs"
          :medObj="selectedMedicationForHold"
          @submit="handleHoldSubmit"
          @cancel="handleHoldCancel"
          @submit-schedule="handleHoldSubmitSchedule"
        />
      </div>
    </div>
    <template v-if="isMobile()">
      <!-- Active Medications -->
      <div v-if="activeMeds && activeMeds.length > 0" class="table-container">
        <div class="accordion">
          <div
            v-for="med in activeMeds"
            :key="'med' + med.medentryid"
            :id="'med' + med.medentryid"
            @click="showMed(med.medentryid)"
            class="accordion-item"
          >
            <button class="accordion-button">
              <span class="med-link-mobile">{{ med.shorthand ? med.shorthand : med.medname }}</span>
            </button>
            <div class="accordion-content">
              <p><b>Medication: </b>{{ `${med.medname}` }}</p>
              <p><b>Amount: </b>{{ `${med.med_amount}` }}</p>
              <p><b>Frequency: </b>{{ `${med.med_frequency}` }}</p>
              <p><b>Diagnosis: </b>{{ `${med.diagnose_code}` }}</p>
              <p><b>Route: </b>{{ `${med.route}` }}</p>
              <p><b>Source: </b>
                <router-link v-if="isCdaSourceLink(med)" :to="med.link" class="cda-source-link">CDA</router-link>
                <span v-else>{{ formatMedSource(med.source) }}</span>
              </p>
              <p><b>Nurse Signature: </b>
                <span v-if="med.order_number && med.order_number !== 0 && (!med.nursesignature || med.nursesignature === '') && med.providersignature && med.providersignature !== ''" 
                      class="sig-card" 
                      @click.stop="openOrderModal(med.order_number, med.medentryid)">
                  <div class="signature-badge" role="img" aria-label="Nurse signature required">
                    <span class="sig-text">Nurse<br>Signature</span>
                    <svg class="pen" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style="transform: scaleX(-1); transform-origin: center;"> 
                      <g transform="translate(24,0) scale(-1,1)"> <path fill="white" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/> 
                      </g> 
                    </svg>
                  </div>
                </span>
                <span v-else>{{ (med.order_number && med.order_number !== 0) ? (med.nursesignature || '') : 'N/A' }}</span>
              </p>
              <p><b>Provider Signature: </b>
                <span v-if="med.order_number && med.order_number !== 0 && med.nursesignature && med.nursesignature !== '' && (!med.providersignature || med.providersignature === '')" 
                      class="sig-card" 
                      @click.stop="openOrderModal(med.order_number, med.medentryid)">
                  <div class="signature-badge" role="img" aria-label="Provider signature required">
                    <span class="sig-text">Provider<br>Signature</span>
                    <svg class="pen" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style="transform: scaleX(-1); transform-origin: center;"> 
                      <g transform="translate(24,0) scale(-1,1)"> <path fill="white" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/> 
                      </g> 
                    </svg>
                  </div>
                </span>
                <span v-else>{{ (med.order_number && med.order_number !== 0) ? (med.providersignature || '') : 'N/A' }}</span>
              </p>
              <p>{{ `${med.med_startdate} to ${formatEndDate(med.med_enddate)}` }}</p>
              <!-- Mobile path: convert unordered/import med into a full order -->
              <button
                v-if="canCreateOrderForMed(med)"
                type="button"
                class="btn-create-order-mobile"
                @click.stop="startCreateOrderFromImport(med)"
              >
                Create Order
              </button>
              <button
                v-if="canEditOrderedMed(med)"
                type="button"
                class="btn-edit-ordered-mobile"
                @click.stop="openOrderedMedicationEdit(med)"
              >
                Edit Medication
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <!-- Active Medications Table -->
      <div v-if="activeMeds && activeMeds.length > 0" class="container">
        <table>
          <thead>
            <tr>
              <th style="width: 20%;">Medication</th>
              <th>Dosage</th>
              <th>Frequency</th>
              <th>Quantity</th>
              <th>Diagnosis</th>
              <th>Start to End</th>
              <th>Route</th>
              <th>Nurse</th>
              <th>Physician</th>
              <th>Source</th> <!-- CDA vs Manual; CDA is clickable when link is stored -->
            </tr>
          </thead>
          <tbody>
            <tr v-for="med in activeMeds" :key="'med' + med.medentryid">
              <td data-label="Medication" style="width: 20%;">
                <a
                  href="#"
                  class="med-link"
                  :class="{ 'med-link--disabled': isOrderedMed(med) && !canMedicationEditOrdered }"
                  @click.prevent="handleMedLinkClick(med)"
                >{{ med.medname }}</a>
              </td>
              <td data-label="Amount">{{ med.med_amount }}</td>
              <td data-label="Frequency">{{ med.med_frequency }}</td>
              <td data-label="Quantity">{{ med.available }}</td>
              <td data-label="Diagnosis">{{ med.diagnose_code }}</td>
              <td data-label="Start to End">
                {{
                  `${med.med_startdate ? med.med_startdate : "—"} to ${
                    formatEndDate(med.med_enddate)
                  }`
                }}
              </td>
              <td data-label="Route">{{ med.route }}</td>
              <td data-label="Nurse" style="position: relative; vertical-align: middle;">
                <span v-if="med.order_number && med.order_number !== 0 && (!med.nursesignature || med.nursesignature === '') && med.providersignature && med.providersignature !== ''" 
                      class="sig-card" 
                      @click="openOrderModal(med.order_number, med.medentryid)">
                  <div class="signature-badge" role="img" aria-label="Nurse signature required">
                    <span class="sig-text">Nurse<br>Signature</span>
                    <svg class="pen" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style="transform: scaleX(-1); transform-origin: center;"> 
                      <g transform="translate(24,0) scale(-1,1)"> <path fill="white" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/> 
                      </g> 
                    </svg>
                  </div>
                </span>
                <span v-else>{{ (med.order_number && med.order_number !== 0) ? (med.nursesignature || '') : 'N/A' }}</span>
              </td>
              <td data-label="Physician" style="position: relative; vertical-align: middle;">
                <span v-if="med.order_number && med.order_number !== 0 && med.nursesignature && med.nursesignature !== '' && (!med.providersignature || med.providersignature === '')" 
                      class="sig-card" 
                      @click="openOrderModal(med.order_number, med.medentryid)">
                  <div class="signature-badge" role="img" aria-label="Provider signature required">
                    <span class="sig-text">Provider<br>Signature</span>
                    <svg class="pen" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" style="transform: scaleX(-1); transform-origin: center;"> 
                      <g transform="translate(24,0) scale(-1,1)"> <path fill="white" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/> 
                      </g> 
                    </svg>
                  </div>
                </span>
                <span v-else>{{ (med.order_number && med.order_number !== 0) ? (med.providersignature || '') : 'N/A' }}</span>
              </td>
              <td data-label="Source">
                <router-link v-if="isCdaSourceLink(med)" :to="med.link" class="cda-source-link">CDA</router-link>
                <span v-else>{{ formatMedSource(med.source) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    
    <!-- Medication Info Modal -->
    <div
      v-if="selectedMed && medInfoForm && showMedInfoModal && !isMobile()"
      class="med-info-modal-overlay"
      @click.self="closeMedInfo"
    >
      <div class="med-info-modal">
        <div class="form-actions">
          <button class="btn-cancel" @click="closeMedInfo">
            Cancel
          </button>
          <!-- Desktop info modal: launch full order form from an unordered/import row -->
          <button
            v-if="canCreateOrderFromImport && !isEditingMedInfo"
            type="button"
            class="btn-create-order"
            @click="startCreateOrderFromImport()"
          >
            Create Order
          </button>
          <button
            v-if="canEditSelectedMed && !isEditingMedInfo"
            type="button"
            class="btn-edit"
            @click="startMedInfoEdit"
          >
            Edit
          </button>
          <button
            v-if="isEditingMedInfo"
            type="button"
            class="btn-save"
            :disabled="isSavingMedInfo"
            @click="saveMedInfoEdit"
          >
            Save
          </button>
        </div>
        <div class="med-info-content">
          <form class="new-med-form" @submit.prevent>
            <h3 class="section-title med-info-section-title">Medication Information</h3>

            <div class="form-group autocomplete">
              <label>Medication Name</label>
              <input
                v-model="medInfoForm.medname"
                type="text"
                :readonly="!isEditingMedInfo"
                :class="['autocomplete-input', { 'readonly-input': !isEditingMedInfo }]"
              />
            </div>

            <div class="form-row">
              <div class="form-group dosage-group">
                <label>Dosage</label>
                <div class="dosage-row">
                  <input
                    v-model="medInfoForm.med_amount"
                    type="number"
                    min="0"
                    :readonly="!isEditingMedInfo"
                    :class="['dosage-input', { 'readonly-input': !isEditingMedInfo }]"
                  />
                </div>
              </div>
              <div class="form-group">
                <label>Dosage Form</label>
                <select
                  v-model="medInfoForm.med_doseuom"
                  class="dosage-select"
                  :disabled="!isEditingMedInfo"
                  :class="{ 'readonly-input': !isEditingMedInfo }"
                >
                  <option value="">Select Dosage Form</option>
                  <option
                    v-for="option in medDosageFormOptions"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>Frequency</label>
                <select
                  v-model="medInfoForm.med_frequency"
                  :disabled="!isEditingMedInfo"
                  :class="{ 'readonly-input': !isEditingMedInfo }"
                  style="width:90%;"
                >
                  <option value="">Select frequency</option>
                  <option
                    v-for="option in medFrequencyOptions"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Route</label>
                <select
                  v-model="medInfoForm.route"
                  :disabled="!isEditingMedInfo"
                  :class="{ 'readonly-input': !isEditingMedInfo }"
                >
                  <option value="">Select route</option>
                  <option
                    v-for="option in medRouteOptions"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <!-- Show NDC persisted from CDA import on the lightweight med info modal. -->
                <label>NDC Number</label>
                <input
                  v-model="medInfoForm.ndcnumber"
                  type="text"
                  :readonly="!isEditingMedInfo"
                  :class="{ 'readonly-input': !isEditingMedInfo }"
                />
              </div>
              <div class="form-group">
                <!-- Show RxNorm persisted from CDA import on the lightweight med info modal. -->
                <label>RX Norm</label>
                <input
                  v-model="medInfoForm.rxnorns"
                  type="text"
                  :readonly="!isEditingMedInfo"
                  :class="{ 'readonly-input': !isEditingMedInfo }"
                />
              </div>
              <div class="form-group">
                <label>Diagnosis</label>
                <input
                  v-model="medInfoForm.diagnose_code"
                  type="text"
                  :readonly="!isEditingMedInfo"
                  :class="{ 'readonly-input': !isEditingMedInfo }"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group checkbox-group">
                <input
                  v-model="medInfoForm.prn"
                  type="checkbox"
                  :disabled="!isEditingMedInfo"
                />
                <label>PRN (As Needed)</label>
              </div>
              <div class="form-group checkbox-group">
                <input
                  v-model="medInfoForm.newMedication"
                  type="checkbox"
                  :disabled="!isEditingMedInfo"
                />
                <label>New Medication</label>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group checkbox-group">
                <input
                  v-model="medInfoForm.medadmin"
                  type="checkbox"
                  :disabled="!isEditingMedInfo || initialMedInfoFlags.medadmin"
                />
                <label>Administration</label>
              </div>
              <div class="form-group checkbox-group">
                <input
                  v-model="medInfoForm.prepour"
                  type="checkbox"
                  :disabled="!isEditingMedInfo || initialMedInfoFlags.prepour"
                />
                <label>Prepour</label>
              </div>
              <div class="form-group checkbox-group">
                <input
                  v-model="medInfoForm.nebulizer"
                  type="checkbox"
                  :disabled="!isEditingMedInfo || initialMedInfoFlags.nebulizer"
                />
                <label>Nebulizer</label>
              </div>
            </div>

            <div v-if="medInfoForm.route === 'IV (Intravenous)'">
              <h4>IV Administration</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Fluid Type</label>
                  <select
                    v-model="medInfoForm.fluidType"
                    :disabled="!isEditingMedInfo"
                    :class="{ 'readonly-input': !isEditingMedInfo }"
                  >
                    <option value="">Select fluid type</option>
                    <option
                      v-for="option in medFluidTypeOptions"
                      :key="option"
                      :value="option"
                    >
                      {{ option }}
                    </option>
                  </select>
                </div>
                <div class="form-group">
                  <label>VIA</label>
                  <select
                    v-model="medInfoForm.via"
                    :disabled="!isEditingMedInfo"
                    :class="{ 'readonly-input': !isEditingMedInfo }"
                  >
                    <option value="">Select an option</option>
                    <option
                      v-for="option in medViaOptions"
                      :key="option"
                      :value="option"
                    >
                      {{ option }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="form-row volume-rate-row">
                <div class="form-group volume-group">
                  <label>Total Volume</label>
                  <div class="volume-row">
                    <input
                      v-model="medInfoForm.totalVolume"
                      list="medInfoVolumeOptions"
                      class="volume-dropdown"
                      placeholder="e.g. 100"
                      :readonly="!isEditingMedInfo"
                      :class="{ 'readonly-input': !isEditingMedInfo }"
                    />
                    <datalist id="medInfoVolumeOptions">
                      <option
                        v-for="value in medVolumeSuggestions"
                        :key="value"
                        :value="value"
                      />
                    </datalist>
                    <select
                      v-model="medInfoForm.totalVolumeUnit"
                      class="volume-dropdown"
                      :disabled="!isEditingMedInfo"
                      :class="{ 'readonly-input': !isEditingMedInfo }"
                    >
                      <option
                        v-for="unit in medVolumeUnitOptions"
                        :key="unit"
                        :value="unit"
                      >
                        {{ unit }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="form-group">
                  <label>Rate</label>
                  <input
                    v-model="medInfoForm.rate"
                    type="text"
                    placeholder="50 (ml/hr)"
                    :readonly="!isEditingMedInfo"
                    :class="{ 'readonly-input': !isEditingMedInfo }"
                  />
                </div>
                <div class="form-group">
                  <label>How Long (hrs)</label>
                  <input
                    type="text"
                    :value="medInfoForm.howLong"
                    disabled
                    placeholder="Computed"
                    class="readonly-input"
                  />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Start Time</label>
                  <input
                    v-model="medInfoForm.startTime"
                    type="time"
                    :readonly="!isEditingMedInfo"
                    :class="{ 'readonly-input': !isEditingMedInfo }"
                  />
                </div>
                <div class="form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    :value="medInfoForm.endTime"
                    disabled
                    class="readonly-input"
                  />
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Start Date</label>
                <input
                  v-if="isEditingMedInfo"
                  v-model="medInfoForm.med_startdate"
                  type="date"
                />
                <input
                  v-else
                  type="text"
                  :value="medInfoForm.med_startdate || ''"
                  readonly
                  class="readonly-input"
                />
              </div>
              <div class="form-group">
                <label>End Date</label>
                <input
                  v-if="isEditingMedInfo"
                  v-model="medInfoForm.med_enddate"
                  type="date"
                />
                <input
                  v-else
                  type="text"
                  :value="formatEndDate(medInfoForm.med_enddate)"
                  readonly
                  class="readonly-input"
                />
              </div>
            </div>

            <div class="form-group">
              <label>Instructions</label>
              <textarea
                v-model="medInfoForm.instruction"
                :readonly="!isEditingMedInfo"
                :class="{ 'readonly-input': !isEditingMedInfo }"
                rows="3"
              ></textarea>
            </div>
          </form>
        </div>
      </div>
    </div>
    <MedadminshortOrderTemplate
      v-model:modelValue="showOrderModal"
      :order="selectedOrder"
      @not-approve-order="handleDCOrder"
      @cancelMedHolds="clearMedicationHold"
      @submitsig="handleSubmit"
    />
  </div>
</template>
<script>
//import {useStore} from "vuex"
import { CURR_API, OTHER_API } from "@/constants/apis";
import axios from "axios";
import {computed} from "vue";
import { mapGetters } from "vuex";
import { medTimes } from "@/constants/med-times";
import { getDescByCcd } from "@/data/getters";
import AddMedicationForm from './AddMedicationForm.vue';
import MedadminshortOrderTemplate from './MedadminshortOrderTemplate.vue';
import HoldTimeSelector from './HoldTimeSelector.vue';
import HelpIcon from '@/components/help/HelpIcon.vue';
import Swal from 'sweetalert2';
import { usePrivilegesStore } from "@/stores/privileges";
import { PRIVILEGES } from "@/constants/privileges";
import { assertPrivilegeOrAlert } from "@/utils/privilegeGate";
import { resolveKeyonPrivilegeIdentity } from "@/api/privilegesApi";
import { keyonRefusalMessage } from "@/utils/keyonRefusal";
import { latestMedsByStatus } from "@/utils/currentMedsLatest";
// CDA import display helpers + import-to-order conversion helpers.
import {
  ensureSelectOption,
  isImportedMed,
  isOrderedMed as checkOrderedMed,
  mapMedRowToEditFormdata,
  mapMedRowToOrderPrefill,
} from "@/utils/cdaMedImportMapper";
import {
  buildHoldSchedulePayload,
  buildHoldSubmitPayload,
  buildHoldTimes,
  checkMedActiveDosageStatus,
  checkMedActiveStatus,
  checkMedHoldDatesUpdate,
  fetchMedAdminRecord,
  holdMedication,
  saveOrderedMedicationEdit,
  scheduleHoldMedication,
} from "@/utils/medicationOrderedEdit";
//const store = useStore()
//const allData = computed(() =>  store.getters?.['login/getAllData'])

const MED_FREQUENCY_OPTIONS = [
  '1 time daily', '2 times daily', '3 times daily', '4 times daily',
  'as directed', 'as needed', 'as one dose', 'at bedtime', 'before every meal',
  'bi-weekly', 'constant infusion', 'daily', 'daily as directed', 'every day',
  'every month', 'every other day', 'every morning', 'every evening', 'every hour',
  'every 2 hours', 'every 3 hours', 'every 4 hours', 'every 4 to 6 hours, as needed',
  'every 4 to 6 minutes', 'every 4 to 8 hours', 'every 6 hours', 'every 8 hours',
  'every 12 hours', 'every 24 hours', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday', 'Sunday', 'every Monday, Wednesday, Friday, Sunday',
  'every Tuesday, Thursday, Saturday', 'before breakfast, lunch, dinner',
  'after breakfast, lunch, dinner', 'once a week', 'one time dose', 'three times a week',
  'twice daily', 'two times a week', 'use as directed per instructions in pack', 'weekly',
];
const MED_ROUTE_OPTIONS = [
  'Oral', 'Sublingual', 'IVI Intravaginal', 'SQ (Subcutaneous)', 'IM (Intramuscular)',
  'IV (Intravenous)', 'ID (Intradermal)', 'TOP Topical', 'Neb/INH', 'NAS Intranasal',
  'TD Transdermal', 'Urethral', 'Rectally', 'Optic', 'Otic',
];
const MED_DOSAGE_FORM_OPTIONS = [
  'Actuation', 'Ampule', 'Application', 'Applicator', 'Auto-Injector', 'Bar', 'Capful', 'Caplet', 'Capsule',
  'Cartridge', 'Centimeter', 'Disk', 'Dropperful', 'Each', 'Film', 'Fluid Ounce', 'Gallon', 'Gram', 'Gum', 'Implant',
  'Inch', 'Inhalation', 'Injection', 'Insert', 'Liter', 'Lollipop', 'Lozenge', 'Metric Drop', 'Microgram',
  'Milliequivalent', 'Milligram', 'Milliliter', 'Nebule', 'Ounce', 'Package', 'Packet', 'Pad', 'Patch', 'Pellet',
  'Pill', 'Pint', 'Pre-filled Pen Syringe', 'Puff', 'Pump', 'Ring', 'Sachet', 'Scoopful', 'Sponge', 'Spray', 'Stick',
  'Strip', 'Suppository', 'Swab', 'Syringe', 'Tablet', 'Troche', 'Unit', 'Vial', 'Wafer',
];
const MED_FLUID_TYPE_OPTIONS = [
  '0.9% Normal Saline', 'D5W (5% Dextrose in Water)', 'Lactated Ringers (LR)', 'Half Normal Saline (0.45% NaCl)',
];
const MED_VIA_OPTIONS = [
  'Peripheral IV - Left Arm', 'Peripheral IV - Right Arm', 'PICC Line - Left', 'PICC Line - Right',
  'Mid Line - Left', 'Mid Line - Right', 'Central Line - Left', 'Central Line - Right',
];
const MED_VOLUME_UNIT_OPTIONS = ['ml', 'liter'];
const MED_VOLUME_SUGGESTIONS = ['10', '100', '250', '500', '1000'];

export default {
  name: "CurrentMed",
  components: {
    AddMedicationForm,
    MedadminshortOrderTemplate,
    HoldTimeSelector,
    HelpIcon,
  },
  data() {
    return {
      mT: null,
      allMeds: null,
      manualAllergies: [],
      showAddMedForm: false,
      isSavingMedication: false,
      errorMessage: "",
      orderPrefillData: null, // prefill payload passed to AddMedicationForm when ordering from import
      convertSourceLink: '', // CDA deep link preserved when converting import row to order
      convertSourceMedentryid: null, // medentryid of the import row to delete after order save
      isOrderingFromImport: false, // true while AddMedicationForm is open for import-to-order flow
      showEditMedForm: false,
      editFormdata: null,
      ifStatusIsChange: false,
      changeActiveMed: false,
      changeReason: '',
      selectedDosage: '1',
      selectedFrequency: '',
      selectedMedicationForTime: null,
      timeInputs: [],
      showHoldSelector: false,
      selectedMedicationForHold: null,
      selectedStatusOption: '',
      holdSelectorCancelledCounter: 0,
      showMedInfoModal: false,
      selectedMed: null,
      medInfoForm: null,
      /** Flags already on when the med-info modal opened — those stay locked while editing. */
      initialMedInfoFlags: {
        medadmin: false,
        prepour: false,
        nebulizer: false,
      },
      isEditingMedInfo: false,
      isSavingMedInfo: false,
      showOrderModal: false,
      selectedOrder: null,
      pastProvar:[],
      pastProvloaded:false,
      medFrequencyOptions: MED_FREQUENCY_OPTIONS,
      medRouteOptions: MED_ROUTE_OPTIONS,
      medDosageFormOptions: MED_DOSAGE_FORM_OPTIONS,
      medFluidTypeOptions: MED_FLUID_TYPE_OPTIONS,
      medViaOptions: MED_VIA_OPTIONS,
      medVolumeUnitOptions: MED_VOLUME_UNIT_OPTIONS,
      medVolumeSuggestions: MED_VOLUME_SUGGESTIONS,
    };
  },
  async mounted() {
    this.loadMedications();
    this.getAllergies();
    this.mT = medTimes;
  
  },
  computed: {
    canOrderSign() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.ORDER_SIGN);
    },
    canMedicationEditOrdered() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.MEDICATION_EDIT_ORDERED);
    },
    canMedicationHold() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.MEDICATION_HOLD);
    },
    canMedicationDiscontinue() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.MEDICATION_DISCONTINUE);
    },
    holdTimes() {
      return buildHoldTimes(this.selectedMedicationForHold);
    },
    canEditSelectedMed() {
      if (!this.selectedMed) return false;
      const orderNumber = this.selectedMed.order_number;
      return orderNumber === 0 || orderNumber === "0" || orderNumber == null;
    },
    // Whether the selected med in the info modal can be converted to an order.
    canCreateOrderFromImport() {
      return this.selectedMed && this.canCreateOrderForMed(this.selectedMed);
    },
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    patient: function () {
      return this.allData.patient;
    },
    allergies: function () {
      let groupings = this.allData.allergyGroupings;
      let result = [];
      if (groupings) {
        groupings.forEach((allergy) => {
          result.push(getDescByCcd(allergy.codes[0], this.allData));
        });
      }
      return result;
    },
    activeMeds: function () {
      if (!this.allMeds) return null;
      return this.getLatestMedsByStatus(false);
    },
  },
  methods: {
    normalizeTimeForInput(value) {
      if (!value) return "";
      const str = String(value);
      return str.length >= 5 ? str.substring(0, 5) : str;
    },
    getMedAdmin() {
      return {
        name: "MedAdminNavigation",
      };
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    },
    async getAllergies() {
      let content = {
        Allergy: {
          API_Meth: "GetAllergies",
          pid: this.allData.patient.externId,
        },
      };
      await axios
        .post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        })
        .then((res) => {
          let result = res.data.allergies;
          if (result) {
            result.forEach((aller) => {
              this.manualAllergies.push(aller.name);
            });
          }
        });
    },
    async loadMedications() {
      let content = {
        MedGrid: {
          API_Meth: "PopulateMedGrid",
          pid: this.allData.patient.externId,
        },
      };
      await axios
        .post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        })
        .then((res) => {
          console.log(res);
          if (res.data.gridhtml) {
            this.allMeds = res.data.data;
          } else {
            console.log("Error loading meds for " + this.patient.externId);
            this.allMeds = null;
          }
        });
    },
    //Adding Cancel Hold Methods 
     //--2/11/16 Clear Change Order Hold Functions ----/// 
  async  clearMedicationHold(ordernumber,medentryid,medname)
  {
    try {
        //lets make call to server
        let content = {
          MedicationAdmin:{
            API_Meth:"ClearCurrentMedicationHold",
            accountnumber:this.allData.patient.externId2,//"904575107",
            npinumber:this.allData.manager.externid || '',
            patientid:this.allData.patient.externId,//"709081242",
            ordernumber:ordernumber,//"36",
            medentryid:medentryid,
            medname:medname,
            provsubaccount:this.allData.manager.externid
          }
        }
        axios.post(CURR_API + '/tswebhook.php',content)
        .then(response => {
          console.log(response.data.result);

          // any release, not only a scheduled one - and a reply that is not a success says why (it was silent)
          if(response.data && response.data.status=="200-Successfull" && response.data.result?.message=="Auto Change Completed successfully")
              {
                alert("The hold on "+medname+" has been released.");
                this.showOrderModal=false //close modal
                this.allMeds = null; //reset
                this.loadMedications();
              }
              else {
                alert(keyonRefusalMessage(response.data));
              }
              
        })
        .catch(error => {
          if (axios.isAxiosError(error)) {  
                alert(keyonRefusalMessage(error));
                  console.error('Error posting data:', error.response?.data || error.message);     
                //  errorMessage = error.response?.data?.message || 'An error occurred while posting data.';  
                } 
                  else {       
                    alert("Opps, we hit an unexpectant snag, please contact your system administrator if this error presist.");   
                    console.log('Unexpected error:', error);         
                  //  errorMessage = 'An unexpected error occurred. Please try again.'; 
                  }    
        });
  
  
      } catch (err) {
      console.error('Failed to load order', err)
      }
  },
    //Adding function to DC Medications 
    async handleDCOrder(reasontext,ordNum,medID,medName,medobj)
  {
    //build object 
    console.log("Handle DC Order Method");
    console.log(medobj);
    console.log(ordNum);
    console.log(medID);
  
    const dcdataobj ={
      medname:medName,
      medentryid:medID || medobj.medentryid ||'',
      reason: reasontext,
      type:"all",
      ordernumber:ordNum || medobj.ordernumber ||medobj.order_number ||'',
      status:'discontinue',
      originalObj:medobj
    }
    console.log("Info from the DcOrderTemplate");
    console.log(dcdataobj);
    let content = {
      MedicationAdmin:{
      API_Meth:"DiscontinueOrder",
      accountnumber:this.allData.patient.externId2,
      npinumber:this.allData.manager.externid ||'',
      patientid:this.allData.patient.externId,
      holdobjec:dcdataobj,
      provsubaccnt:this.allData.manager.externid
    }
    }
    //make server call
    axios.post(CURR_API + '/tswebhook.php',content)
    .then(response => {
      console.log(response.data);
      if(response.data.code && response.data.code =="200-Successfull")
        {
          let returnmsg="";
          returnmsg=response.data.message;
          alert(returnmsg);
          //showAddForm.value = false;
          this.showOrderModal = false; //closeing the Order Sign oFf modal
          this.allMeds = null;//reset the medlist before calling to load meds again to give perception of realtime updating
          this.loadMedications();
        // populateMedicationTable();
          return ;

        }
        else if(response.data.code && response.data.code =="304-CodeError" && response.data.error){
          //now lets add the information to the Appropriate tables 
          
          alert(response.data.error);
          console.log("Error Message from Discontinue Order");
          console.log(response.data.errMsg);
          //alert("Meciation Settings Not Updated Successfully, Please try again later");
        }
    })
    .catch(error => {
      if (axios.isAxiosError(error)) {  
            alert(keyonRefusalMessage(error));
              console.error('Error posting data:', error.response?.data || error.message);     
            //  errorMessage = error.response?.data?.message || 'An error occurred while posting data.';  
            } 
              else {       
                alert("Opps, we hit an unexpectant snag, please contact your system administrator if this error presist.");   
                console.log('Unexpected error:', error);         
              //  errorMessage = 'An unexpected error occurred. Please try again.'; 
              }    
    });
  },
    showAddMed() {
      if (
        !assertPrivilegeOrAlert(
          PRIVILEGES.MEDICATION_ADD_UNORDERED,
          "You do not have permission to add medications."
        )
      ) {
        return;
      }
      // Manual add must not carry over a prior import-to-order prefill.
      this.clearConvertOrderState();
      this.showAddMedForm = true;
    },
    handleCloseMedForm() {
      this.showAddMedForm = false;
      // Discard import-to-order state when the user cancels the order form.
      this.clearConvertOrderState();
    },
    // Reset all state tied to converting an import row into an order.
    clearConvertOrderState() {
      this.orderPrefillData = null;
      this.convertSourceMedentryid = null;
      this.convertSourceLink = '';
      this.isOrderingFromImport = false;
    },
    // Delegate to mapper: unordered rows (order_number 0) are eligible for ordering.
    canCreateOrderForMed(med) {
      return isImportedMed(med);
    },
    // Close info modal and open AddMedicationForm pre-filled from the import row.
    startCreateOrderFromImport(med = null) {
      const sourceMed = med || this.selectedMed;
      if (!sourceMed || !this.canCreateOrderForMed(sourceMed)) {
        return;
      }
      // Ordered med creation requires a different privilege than unordered edit.
      if (
        !assertPrivilegeOrAlert(
          PRIVILEGES.MEDICATION_ADD_ORDERED,
          "You do not have permission to create medication orders."
        )
      ) {
        return;
      }

      const prefill = mapMedRowToOrderPrefill(sourceMed);
      const medentryid = sourceMed.medentryid; // kept so we can delete the import row on save
      const sourceLink = sourceMed.link || '';

      // Tear down the lightweight info modal before opening the full order form.
      this.showMedInfoModal = false;
      this.selectedMed = null;
      this.medInfoForm = null;
      this.isEditingMedInfo = false;

      // nextTick ensures editFormdata watch sees null -> prefill transition for prefill.
      this.$nextTick(() => {
        this.clearConvertOrderState();
        this.convertSourceMedentryid = medentryid;
        this.convertSourceLink = sourceLink;
        this.isOrderingFromImport = true;
        this.orderPrefillData = prefill;
        this.showAddMedForm = true;
      });
    },
    // Remove the unordered import row via DeleteMedication before inserting the real order.
    async deleteImportedMedication(medentryid) {
      const payload = {
        MedicationUpdate: {
          API_Meth: "DeleteMedication",
          patientid: this.allData.patient.externId,
          medentryid,
        },
      };
      const response = await axios.post(CURR_API + "/tswebhook.php", payload);
      if (response.data?.status !== "Deleted") {
        throw new Error(response.data?.msg || "Failed to remove imported medication.");
      }
      return response.data;
    },
    //Addidng to account for Past Providers on this page KW (4/30/26)
    async loadPastProvider()
    {
      console.log("Did we call the function - loadPastProvider");
      try{
    let content = {
      Provider:{
      API_Meth: "GetAllProviders",
      patientid: this.allData.patient.externId,//"709081242", //"70894333",
      accountnumber: this.allData.patient.externId2
      }
    };
    axios.post(CURR_API + '/tswebhook.php', content)
         .then(response => {        
          console.log('Data posted successfully:', response.data);  
         this.pastProvar = response.data.provider;
          console.log(this.pastProvar);
          this.pastProvloaded = true;
          //errorMessage = ""; // Clear any previous error messages     
         }) 
         .catch(error => {       
           if (axios.isAxiosError(error)) {  
             console.error('Error posting data:', error.response?.data || error.message);     
           // errorMessage = error.response?.data?.message || 'An error occurred while posting data.';  
           } 
            else {          
              console.log('Unexpected error:', error);         
             // errorMessage = 'An unexpected error occurred. Please try again.'; 
            }    
            });   
  }
  catch(error)
  {
    console.error("Error Posting to Past Provider enpoint:",error);
  }
    },
    async handleSaveMedication(formData, timeInputs) {
      // Prevent multiple simultaneous saves - check and set flag BEFORE any processing
      if (this.isSavingMedication) {
        console.log('Save already in progress, ignoring duplicate call');
        return;
      }

      // Set loading state IMMEDIATELY to prevent race conditions
      this.isSavingMedication = true;
      
      // Show loading popup immediately
      Swal.fire({
        title: 'Saving medication...',
        text: 'Please wait while the medication is being saved.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Snapshot flag before delete clears convert state; used for success messaging.
      const wasOrderingFromImport = this.isOrderingFromImport;
      const cdaLinkToPreserve = this.convertSourceLink || formData.link || ''; // keep CDA back-link on the new ordered row

      // Delete import row first so InsertAdminMecationInfo does not hit duplicate detection.
      if (this.convertSourceMedentryid) {
        try {
          await this.deleteImportedMedication(this.convertSourceMedentryid);
          this.convertSourceMedentryid = null;
        } catch (deleteError) {
          Swal.close();
          Swal.fire({
            icon: 'error',
            title: 'Could Not Remove Import',
            text: deleteError.message || 'The imported medication could not be removed. Please try again.',
            confirmButtonText: 'Ok',
          });
          this.isSavingMedication = false;
          return; // abort save if the import row could not be removed
        }
      }

      const now = new Date();
      const orderDate = now.toISOString().split('T')[0];
      const orderTime = now.toTimeString().split(' ')[0];

      let medsetting = "";
      
      // Convert timeInputs array to slotedtimes array format
      // Use timeInputs if provided (from emit), otherwise fall back to formData.administrationTimes
      let slotedtimesArray = [];
      if (timeInputs && timeInputs.length > 0) {
        // Use the timeInputs array passed from the form
        slotedtimesArray = timeInputs.filter(t => t && t.trim() !== '').map(t => ({
          time: t.trim(),
          status: 'pending',
          dosage: parseInt(formData.dosage, 10) || 1
        }));
      } else if (formData.administrationTimes && formData.administrationTimes.trim() !== '') {
        // Fallback to administrationTimes string if timeInputs not provided
        const times = formData.administrationTimes.split(',').map(t => t.trim()).filter(t => t);
        slotedtimesArray = times.map(t => ({
          time: t,
          status: 'pending',
          dosage: parseInt(formData.dosage, 10) || 1
        }));
      }
      
      // Store ordernumber for later use in medicationlog creation
      const requestOrdernumber = '36'; // This should ideally come from the response, but for now using placeholder
      
      let content = {
        MedicationAdmin: {
          API_Meth: "InsertAdminMecationInfo", 
          orderDate: orderDate,
          orderTime: orderTime, 
          accountnumber: this.allData.patient.externId2,
          npinumber: formData.providerNpi || formData.providernpi || this.allData.manager.externid || '',
          proflicensenumber: "123123123", // prev nurse # needs items passed from pacmny-fe "609382", //insurance test
          patientid: this.allData.patient.externId,
          ordernumber: requestOrdernumber,
          name: formData.medicationName || '',
          dates: {},
          tabsAvailable: formData.tabsAvailable || 0,
          totalTabs: formData.quantity,
          frequency: formData.frequency || '',
          dosage: formData.dosage || '',
          administrationTimes: formData.administrationTimes || '',
          slotedtimes: slotedtimesArray,
          scheduletimes: formData.administrationTimes,
          route: formData.route || '',
          dosageForm: formData.dosageForm || '',
          diagnosis: formData.diagnosis || '',
          prn: formData.prn,
          newMedication: formData.newMedication || false,
          administration: formData.administration === true || formData.administration === 1 || formData.administration === '1' ? 1 : 0,
          prepour: formData.prepour === true || formData.prepour === 1 || formData.prepour === '1' ? 1 : 0,
          nebulizer: formData.nebulizer === true || formData.nebulizer === 1 || formData.nebulizer === '1' ? 1 : 0,
          datescriptFilled: formData.filledDate,
          startDate: formData.startDate,
          endDate: formData.endDate,
          primeprovName: formData.providerName,
          primeprovEmail: formData.providerEmail,
          primeprovAddress: formData.providerAddress,
          primeprovPhone: formData.providerOffice,
          primeprovFax: formData.providerFax,
          primeprovNpi: formData.providerNpi,
          providerDea: formData.providerDea || '',
          providerCell: formData.providerCell || '',
          pharmacy: formData.pharmacyName || '',
          pharmacyNpi: formData.pharmacyNpi || '',
          pharmacyAddress: formData.pharmacyAddress || '',
          pharmacyPhone: formData.pharmacyOffice || '',
          pharmacyCell: formData.pharmacyCell || '',
          pharmacyEmail: formData.pharmacyEmail || '',
          pharmacyDea: formData.pharmacyDea || '',
          prescriberInfo: formData.prescriberInfo || '',
          prescriberDeaNpi: formData.prescriberDeaNpi || '',
          rxNumber: formData.rxnorns || '',
          ndcnumber: formData.ndcnumber,
          refills: formData.refills || 0,
          refillReminderDate: formData.refillReminderDate,
          expirationDate: formData.expirationDate || '',
          instructions: formData.instructions || '',
          status: 'active',
          medsetting: medsetting,
          discontinuedDate: undefined,
          discontinuedTimes: {},
          via: formData.via || 0,
          fluidType: formData.fluidType,
          startTime: formData.startTime,
          endTime: formData.endTime,
          howLong: formData.howLong || 0,
          totalVolume: formData.totalVolume,
          totalVolumeUnit: formData.totalVolumeUnit,
          rate: formData.rate,
          provaccountnumber: this.allData.manager.externid2,
          provsubaccountnumber: this.allData.manager.externid,
          units: formData.unitType || '',
          source: wasOrderingFromImport ? 'CDA' : 'Manual', // tag provenance in medications.source
          link: wasOrderingFromImport ? cdaLinkToPreserve : '', // /cdas/... path for Source column hyperlink
        }
      };
      
      axios.post(CURR_API + '/tswebhook.php', content)
        .then(response => {        
          console.log('Data posted successfully:', response.data);  
          if(response.data && response.data.count >= 1) {
            /*let the user know that the medication is already in the table and active. Then ask with a confirm dialog if they want to add the Mec to the 
            *Administration setting, so that it can be added from the Medication table 
            */
            console.log("Here now lets ask for the dialog");
            Swal.fire({
              title: 'Duplicate Medication Detected',
              text: formData.medicationName + " is already listed in the Patients Medication List. Do you want to add it to the Administration setting to be included?",
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Yes',
              cancelButtonText: 'No',
              confirmButtonColor: '#4b9ca5',
              cancelButtonColor: '#cb5f50'
            }).then((result) => {
              if (result.isConfirmed) {
                // User clicked Yes - continue with update
                content.MedicationAdmin.medsetting = "update";
                this.updateMedAdminSetting(content).then(() => {
                  // Show success Swal after update completes
                  Swal.fire({
                    icon: 'success',
                    title: 'Medication Updated Successfully',
                    text: 'The medication has been added to the Administration setting.',
                    confirmButtonText: 'Ok'
                  }).then(() => {
                    // Close all popups and reload medications
                    this.showAddMedForm = false;
                    this.clearConvertOrderState(); // clear import-to-order state after duplicate-update path
                    this.loadMedications();
                  });
                }).catch((error) => {
                  // Handle error if update fails
                  Swal.fire({
                    icon: 'error',
                    title: 'Update Failed',
                    text: 'There was an error updating the medication. Please try again.',
                    confirmButtonText: 'Ok'
                  });
                });
              } else {
                // User clicked No - just close the Swal, form stays open
                // Swal will auto-close, no action needed
              }
            });
          } else if(response.data.message == "Medication Added Successfully" && response.data.status == "200 Successfull") {
            // Update allMeds when medication is successfully added
            if(response.data.records) {
              this.allMeds = response.data.records;
            }
            
            // Medicationlog entry should be created by backend InsertAdminMecationInfo if slotedtimes was provided
            // No need for separate frontend call
            Swal.close();
            Swal.fire({
              icon: 'success',
              // Different copy when user converted an import row vs added manually.
              title: wasOrderingFromImport ? 'Medication Ordered Successfully' : 'Medication Added Successfully',
              text: wasOrderingFromImport
                ? 'The medication has been converted to an order.'
                : 'The medication has been saved successfully.',
              confirmButtonText: 'Ok'
            }).then(() => {
              this.showAddMedForm = false;
              this.clearConvertOrderState(); // import row already deleted; reset ordering flags
              this.loadMedications();
            });
            // Reset saving flag
            this.isSavingMedication = false;
          } else {
            // Hide loading popup and show error popup for unexpected response
            Swal.close();
           
            console.log("Unexpected response - there is an error somewhere with the returned payload");
            // Show error popup using Swal directly
            Swal.fire({
              icon: 'error',
              title: 'Error Saving Medication',
              text: 'There was a system error with the returned payload. Please check the logs or try again.',
              confirmButtonText: 'Ok'
            });
            this.errorMessage = "There is a system error - with returned payload. Look into logs";
            // Reset saving flag on error
            this.isSavingMedication = false;
          }
          this.errorMessage = ""; // Clear any previous error messages     
        }) 
        .catch(error => {  
          Swal.close();
            
          let errorText = 'An error occurred while saving the medication. Please try again.';   
          if (axios.isAxiosError(error)) {
            console.error('Error posting data:', error.response?.data || error.message);
            errorText = error.response?.data?.message || errorText;
            this.errorMessage = errorText;
        } else {
            console.log('Unexpected error:', error);
            this.errorMessage = 'An unexpected error occurred. Please try again.';
          }

          Swal.fire({
            icon: 'error',
            title: 'Error Saving Medication',
            text: errorText,
            confirmButtonText: 'Ok'
          });
        })
        .finally(() => {
          // Always reset loading state (loader already hidden in success/error handlers)
          this.isSavingMedication = false;
      });
    },
    async updateMedAdminSetting(payload) {
      return new Promise((resolve, reject) => {
        axios.post(CURR_API + '/tswebhook.php', payload)
          .then(response => {        
            console.log('Past Update parmaaters successfully:', response.data);  
            if(response.data && response.data.message == "Updated Successfully") {
              // Don't close form or load medications here - let the caller handle it
              resolve('Medication Updated Successfully');
            } else {
              // Return success even if message format is different
              resolve('Update Note Successful');
            }
          }) 
          .catch(error => {       
            let errorMessage = '';
            if (axios.isAxiosError(error)) {  
              console.error('Error posting data:', error.response?.data || error.message);     
              errorMessage = error.response?.data?.message || 'An error occurred while posting data.';  
            } 
            else {          
              console.log('Unexpected error:', error);         
              errorMessage = 'An unexpected error occurred. Please try again.'; 
            }
            reject(new Error(errorMessage));
          });   
      });
    },
    showMed(id) {
      let entry = document.getElementById(`med${id}`);
      entry.classList.toggle("active");
    },
    isOrderedMed(med) {
      return checkOrderedMed(med);
    },
    canEditOrderedMed(med) {
      return checkOrderedMed(med) && this.canMedicationEditOrdered;
    },
    handleMedLinkClick(med) {
      if (checkOrderedMed(med)) {
        this.openOrderedMedicationEdit(med);
        return;
      }
      this.showMedInfo(med);
    },
    async openOrderedMedicationEdit(med) {
      if (
        !assertPrivilegeOrAlert(
          PRIVILEGES.MEDICATION_EDIT_ORDERED,
          'You do not have permission to edit ordered medications.'
        )
      ) {
        return;
      }
      try {
        Swal.fire({
          title: 'Loading medication...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => Swal.showLoading(),
        });
        const adminMed = await fetchMedAdminRecord(this.allData, med);
        Swal.close();
        const formData = mapMedRowToEditFormdata(med, adminMed);
        if (!formData) {
          alert('Unable to load medication data for editing.');
          return;
        }
        this.editFormdata = formData;
        this.selectedMedicationForTime = formData;
        this.selectedFrequency = formData.med_frequency || '';
        this.selectedDosage = formData.med_amount || '1';
        this.changeActiveMed = false;
        this.ifStatusIsChange = false;
        this.changeReason = '';
        this.showEditMedForm = true;
      } catch (error) {
        Swal.close();
        const errorText = axios.isAxiosError(error)
          ? error.response?.data?.message || error.message
          : 'Failed to load medication for editing.';
        await Swal.fire({
          icon: 'error',
          title: 'Load Failed',
          text: errorText,
          confirmButtonText: 'Ok',
        });
      }
    },
    handleCloseEditMedForm() {
      this.showEditMedForm = false;
      this.editFormdata = null;
      this.selectedMedicationForTime = null;
      this.timeInputs = [];
      this.changeActiveMed = false;
      this.ifStatusIsChange = false;
      this.changeReason = '';
    },
    async handleEditMedicationSave(medication, nwtimeInputs, freq) {
      if (this.isSavingMedication) return;
      this.isSavingMedication = true;
      this.timeInputs = Array.isArray(nwtimeInputs) ? nwtimeInputs : [];
      try {
        await saveOrderedMedicationEdit({
          medication,
          nwtime: nwtimeInputs,
          selectedFreq: freq,
          allData: this.allData,
          changeActiveMed: this.changeActiveMed,
          changeReason: this.changeReason,
          selectedMedicationForTime: this.selectedMedicationForTime,
          timeInputs: this.timeInputs,
          selectedDosage: this.selectedDosage,
          selectedFrequency: this.selectedFrequency,
          onComplete: () => {
            this.handleCloseEditMedForm();
            this.loadMedications();
          },
        });
      } finally {
        this.isSavingMedication = false;
      }
    },
    onEditFreqChange(_obj, frequency) {
      this.selectedFrequency = frequency;
      const result = checkMedActiveStatus(this.editFormdata, frequency, this.selectedDosage);
      if (result) {
        this.changeActiveMed = result.changeActiveMed;
        this.ifStatusIsChange = result.ifStatusIsChange;
      }
    },
    onEditDosageChange(_obj, dosage) {
      this.selectedDosage = dosage;
      const result = checkMedActiveDosageStatus(
        this.editFormdata,
        dosage,
        this.selectedFrequency
      );
      if (result) {
        this.changeActiveMed = result.changeActiveMed;
        this.ifStatusIsChange = result.ifStatusIsChange;
      }
    },
    onEditHoldDateChange(holdStart, holdEnd) {
      const result = checkMedHoldDatesUpdate(this.editFormdata, holdStart, holdEnd);
      this.changeActiveMed = result.changeActiveMed;
      this.ifStatusIsChange = result.ifStatusIsChange;
    },
    triggerHoldDCModal(activeStatus, formData) {
      if (activeStatus === 'hold' && !this.canMedicationHold) {
        alert('You do not have permission to hold medications.');
        return;
      }
      if (activeStatus === 'discontinue' && !this.canMedicationDiscontinue) {
        alert('You do not have permission to discontinue medications.');
        return;
      }
      this.selectedMedicationForHold = formData;
      this.selectedStatusOption = activeStatus;
      this.showHoldSelector = true;
    },
    handleHoldCancel() {
      this.showHoldSelector = false;
      this.selectedMedicationForHold = null;
      this.holdSelectorCancelledCounter += 1;
    },
    async handleHoldSubmitSchedule(data) {
      if (!this.selectedMedicationForHold) return;
      const medication = this.selectedMedicationForHold;
      const holdInfo = buildHoldSchedulePayload(medication, data);
      let ask = false;
      if (data.statusOption === 'hold') {
        switch (data.action) {
          case 'schedule':
            ask = confirm('Are you sure you want to schedule a hold on this medication?');
            break;
          case 'cancel':
            ask = confirm('Are you sure you want to cancel the hold on this medication?');
            break;
          case 'update':
            ask = confirm(
              'Are you sure you want to Update the Scheduled Hold Dates on this medication?'
            );
            break;
          default:
            break;
        }
      }
      if (!ask) {
        this.handleHoldCancel();
        return;
      }
      if (!holdInfo.ordernumber) {
        alert('Order Number associated with this change does not exist. Please refresh and try again');
        this.handleHoldCancel();
        return;
      }
      try {
        const response = await scheduleHoldMedication(this.allData, holdInfo);
        if (response?.code === '1' && ['Inserted', 'Updated', 'Canceled'].includes(response?.results)) {
          const msg =
            response.results === 'Inserted'
              ? 'Medication Administration Hold times has been scheduled successfully.'
              : response.results === 'Updated'
                ? 'Scheduled/Pending Hold has been successfully updated.'
                : 'Scheduled Hold has been removed successfully.';
          alert(msg);
          this.handleHoldCancel();
          this.handleCloseEditMedForm();
          this.loadMedications();
        } else if (response?.code === '0') {
          alert(response.error || 'Medication hold was not updated.');
        }
      } catch (error) {
        alert(keyonRefusalMessage(error));
      }
    },
    async handleHoldSubmit(data) {
      if (!this.selectedMedicationForHold) return;
      const medication = this.selectedMedicationForHold;
      const holdInfo = buildHoldSubmitPayload(medication, data);
      let ask = false;
      if (data.statusOption === 'discontinue') {
        ask = confirm('Are you sure you want to discontinue this medication?');
      } else if (data.statusOption === 'hold') {
        ask = confirm('Are you sure you want to hold this medication?');
      }
      if (!ask) {
        this.handleHoldCancel();
        return;
      }
      if (!holdInfo.ordernumber) {
        alert('Order Number associated with this change does not exist. Please refresh and try again');
        this.handleHoldCancel();
        return;
      }
      try {
        const response = await holdMedication(this.allData, holdInfo);
        if (response?.results === 'Updated' && response?.message === 'Medication Held Successfully!') {
          const msg =
            holdInfo.status === 'discontinue'
              ? 'Medication Discontinued Successfully'
              : 'Medication Held Successfully';
          alert(msg);
          this.handleHoldCancel();
          this.handleCloseEditMedForm();
          this.loadMedications();
        } else if (response?.error) {
          alert(response.error);
        }
      } catch (error) {
        alert(keyonRefusalMessage(error));
      }
    },
    showMedInfo(med) {
      // Don't show modal on mobile - accordion handles the display
      if (this.isMobile()) {
        return;
      }
      this.selectedMed = med;
      // Add CDA-imported values to dropdowns so they display even when outside defaults.
      this.ensureMedInfoSelectOptions(med);
      this.medInfoForm = this.buildMedInfoFormFromMed(med);
      this.initialMedInfoFlags = {
        medadmin: !!this.medInfoForm.medadmin,
        prepour: !!this.medInfoForm.prepour,
        nebulizer: !!this.medInfoForm.nebulizer,
      };
      this.isEditingMedInfo = false;
      this.showMedInfoModal = true;
    },
    closeMedInfo() {
      this.showMedInfoModal = false;
      this.selectedMed = null;
      this.medInfoForm = null;
      this.initialMedInfoFlags = {
        medadmin: false,
        prepour: false,
        nebulizer: false,
      };
      this.isEditingMedInfo = false;
      this.isSavingMedInfo = false;
      // Reset dynamic dropdown options added for the previously viewed imported med.
      this.medDosageFormOptions = [...MED_DOSAGE_FORM_OPTIONS];
      this.medFrequencyOptions = [...MED_FREQUENCY_OPTIONS];
      this.medRouteOptions = [...MED_ROUTE_OPTIONS];
    },
    // Inject CDA-imported route/dose/frequency values into select lists for display and edit.
    ensureMedInfoSelectOptions(med) {
      if (!med) return;
      if (med.med_doseuom) {
        this.medDosageFormOptions = ensureSelectOption(this.medDosageFormOptions, med.med_doseuom);
      }
      if (med.med_frequency) {
        this.medFrequencyOptions = ensureSelectOption(this.medFrequencyOptions, med.med_frequency);
      }
      if (med.route) {
        this.medRouteOptions = ensureSelectOption(this.medRouteOptions, med.route);
      }
    },
    buildMedInfoFormFromMed(med) {
      // Lightweight edit form for unordered/import rows; includes CDA-persisted ndc/rx/route fields.
      const endDate = med.med_enddate || "";
      const normalizedEndDate =
        !endDate || endDate === "1971-01-01" || endDate === "1970-01-01" ? "" : endDate;

      return {
        medname: med.medname || "",
        med_amount: med.med_amount || "",
        // Show raw imported values; ensureMedInfoSelectOptions adds non-standard options to dropdowns.
        med_doseuom: med.med_doseuom || "",
        med_frequency: med.med_frequency || "",
        route: med.route || "",
        ndcnumber: med.ndcnumber || "", // from CDA import via InsertMedication
        rxnorns: med.rxnorns || "",
        diagnose_code: med.diagnose_code || "",
        prn: med.prn === 1 || med.prn === "1" || med.prn === true,
        newMedication: med.medchangetype === "New",
        medadmin: med.medadmin === 1 || med.medadmin === "1" || med.medadmin === true,
        prepour: med.prepour === 1 || med.prepour === "1" || med.prepour === true,
        nebulizer: med.nebulizer === 1 || med.nebulizer === "1" || med.nebulizer === true,
        fluidType: med.fluidtype || "",
        via: med.viatype || "",
        totalVolume: med.totalVolumn || "",
        totalVolumeUnit: med.totalVolumnUnit || "ml",
        rate: med.rate || "",
        howLong: med.ivhowlong != null && med.ivhowlong !== "" ? String(med.ivhowlong) : "",
        startTime: this.normalizeTimeForInput(med.ivstarttime),
        endTime: this.normalizeTimeForInput(med.ivendtime),
        med_startdate: med.med_startdate || "",
        med_enddate: normalizedEndDate,
        instruction: med.instruction || "",
        alt_route: med.alt_route || "",
      };
    },
    startMedInfoEdit() {
      if (
        !assertPrivilegeOrAlert(
          PRIVILEGES.MEDICATION_EDIT_UNORDERED,
          "You do not have permission to edit medications."
        )
      ) {
        return;
      }
      this.isEditingMedInfo = true;
    },
    buildMedInfoUpdatePayload() {
      const form = this.medInfoForm;
      const { accountnumber, unique_ID } = resolveKeyonPrivilegeIdentity(this.allData);
      const endDate =
        !form.med_enddate || form.med_enddate === "—" ? null : form.med_enddate;
      const isIvRoute = form.route === "IV (Intravenous)";
      const ivHowLong =
        form.howLong !== "" && form.howLong != null ? Number(form.howLong) : null;
      return {
        MedicationEdit: {
          API_Meth: "UpdateMedication",
          patientID: this.allData.patient.externId,
          medentryid: this.selectedMed.medentryid,
          ordernumber: 0,
          medname: form.medname || "",
          doseamount: form.med_amount || "",
          doseUOM: form.med_doseuom || "",
          frequency: form.med_frequency || "",
          prn: form.prn ? 1 : 0,
          diagcode: form.diagnose_code || "",
          medRoute: form.route || "",
          altroute: form.alt_route || "",
          instruction: form.instruction || "",
          medstrtdt: form.med_startdate || null,
          medenddt: endDate,
          changetype: form.newMedication ? "New" : "",
          durgclassification: "",
          directionuse: "",
          // When Administration is newly checked, mirror insert path so MedAdmin treats it as administered.
          additionalsettings: form.medadmin
            ? "Administered"
            : this.selectedMed.additional_settings || "",
          medstatus: "Active",
          ndcnumber: form.ndcnumber || "", // include CDA-imported codes in unordered med updates
          rxnorns: form.rxnorns || "",
          prepour: form.prepour ? 1 : 0,
          nebulizer: form.nebulizer ? 1 : 0,
          medadmin: form.medadmin ? 1 : 0,
          fluidtype: isIvRoute ? form.fluidType || "" : "",
          viatype: isIvRoute ? form.via || "" : "",
          totalvolume: isIvRoute ? form.totalVolume || null : null,
          totalvolumeunit: isIvRoute ? form.totalVolumeUnit || "" : "",
          rate: isIvRoute ? form.rate || null : null,
          ivhowlong: isIvRoute ? ivHowLong : null,
          ivstarttime: isIvRoute ? form.startTime || null : null,
          ivendtime: isIvRoute ? form.endTime || null : null,
          accountnumber,
          subaccountnumber: unique_ID,
        },
      };
    },
    async saveMedInfoEdit() {
      if (!this.isEditingMedInfo || this.isSavingMedInfo || !this.medInfoForm) {
        return;
      }
      this.isSavingMedInfo = true;
      try {
        const response = await axios.post(
          CURR_API + "/tswebhook.php",
          this.buildMedInfoUpdatePayload(),
          { headers: { "Content-Type": "application/json;" } }
        );
        const result = response.data;
        const parsed = typeof result === "string" ? JSON.parse(result) : result;
        const updateMsg = parsed?.msg || "";
        if (updateMsg === "Updated") {
          this.isEditingMedInfo = false;
          await this.loadMedications();
          const updatedMed = this.allMeds?.find(
            (m) => m.medentryid === this.selectedMed.medentryid
          );
          if (updatedMed) {
            this.selectedMed = updatedMed;
            this.ensureMedInfoSelectOptions(updatedMed); // refresh dropdowns after edit save
            this.medInfoForm = this.buildMedInfoFormFromMed(updatedMed);
            // Newly checked flags become locked on the next edit.
            this.initialMedInfoFlags = {
              medadmin: !!this.medInfoForm.medadmin,
              prepour: !!this.medInfoForm.prepour,
              nebulizer: !!this.medInfoForm.nebulizer,
            };
          }
          await Swal.fire({
            icon: "success",
            title: "Medication Updated",
            text: "The medication was saved successfully.",
            confirmButtonText: "Ok",
          });
        } else {
          const errorText =
            (typeof updateMsg === "string" && updateMsg.includes("PDOException")
              ? "A database error occurred while saving the medication."
              : null) ||
            parsed?.error ||
            updateMsg ||
            "The medication could not be updated.";
          await Swal.fire({
            icon: "error",
            title: "Update Failed",
            text: errorText,
            confirmButtonText: "Ok",
          });
        }
      } catch (error) {
        const errorText = axios.isAxiosError(error)
          ? error.response?.data?.message || error.message
          : "An unexpected error occurred while saving.";
        await Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: errorText,
          confirmButtonText: "Ok",
        });
      } finally {
        this.isSavingMedInfo = false;
      }
    },
    formatEndDate(endDate) {
      if (!endDate || endDate === '1971-01-01' || endDate === '1970-01-01') {
        return '—';
      }
      return endDate;
    },
    // Display medications.source in the grid (CDA, Manual, or em dash).
    formatMedSource(source) {
      const value = String(source || '').trim();
      if (value === 'CDA' || value === 'Manual') {
        return value;
      }
      return '—';
    },
    // True when Source column should render as a link back to the originating CDA entry.
    isCdaSourceLink(med) {
      return med && med.source === 'CDA' && med.link && String(med.link).trim() !== '';
    },
    getLatestMedsByStatus(isDiscontinued) {
      return latestMedsByStatus(this.allMeds, isDiscontinued);
    },
    async openOrderModal(orderId, medId) {
      if (!this.canOrderSign) {
        alert("You do not have permission to sign off on orders.");
        return;
      }
      try {
        let content = {
          MedicationAdmin: {
            API_Meth: "LookupOrderInfoSigSignOff",
            accountnumber: this.allData.patient.externId2,
            npinumber: this.allData.manager.externid || '',
            patientid: this.allData.patient.externId,
            ordernumber: orderId,
            medentryid: medId,
            provsubaccount: this.allData.manager.externid,
            type: "medication"
          }
        };
        await axios.post(CURR_API + '/tswebhook.php', content)
          .then(response => {
            console.log(response.data);
            if (response.data && response.data.code == "200 Successfull" && response.data.results) {
              this.selectedOrder = response.data.results[0];
              console.log("Order LookUp Results");
              console.log(this.selectedOrder);
            }
          })
          .catch(error => {
            if (axios.isAxiosError(error)) {
              alert(keyonRefusalMessage(error));
              console.error('Error posting data:', error.response?.data || error.message);
            } else {
              alert("Oops, we hit an unexpected snag, please contact your system administrator if this error persists.");
              console.log('Unexpected error:', error);
            }
          });
        console.log("Order Id For the Order Template");
        console.log(orderId);
        console.log("Med ID For Order Template");
        console.log(medId);
        this.showOrderModal = true;
      } catch (err) {
        console.error('Failed to load order', err);
      }
    },
    handleSubmit(nurseSignature) {
      console.log(nurseSignature);
      let content = {
        MedicationAdmin: {
          API_Meth: "UpdateOrderWithNurseSignature",
          accountnumber: this.allData.patient.externId2,
          patientid: this.allData.patient.externId,
          ordernumber: nurseSignature.ordernumber,
          nurseSignature: nurseSignature.nurseSignature,
          nurseSigDate: nurseSignature.nurseSignedDate,
          providerSignature: nurseSignature.provider,
          providerSignedDate: nurseSignature.providerSignedDate,
          action: nurseSignature.action,
          provsubaccount: this.allData.manager.externid
        }
      };
      axios.post(CURR_API + '/tswebhook.php', content)
        .then(response => {
          console.log(response.data);
          if (response.data && (response.data.code == "200 Successfull" || response.data.code == "200-Sql Success")) {
            alert("Signature updated successfully");
            this.loadMedications();
            this.showOrderModal = false;
          } else {
            alert("Error updating signature. Please try again.");
          }
        })
        .catch(error => {
          if (axios.isAxiosError(error)) {
            alert(keyonRefusalMessage(error));
            console.error('Error posting data:', error.response?.data || error.message);
          } else {
            alert("Oops, we hit an unexpected snag, please contact your system administrator if this error persists.");
            console.log('Unexpected error:', error);
          }
        });
    },
  },
};
</script>
<style scoped lang="scss">
.header {
  text-align: center;
  padding: 10px 0;
  font-size: 1.5em;
  margin-top: 5%;
}
.medications-header {
  margin-top: 20px;
  margin-bottom: 20px;
}

.medications-title {
  text-align: center;
  margin: 0 0 15px 0;
  font-size: 2.5em;
}

.medications-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.nav-link {
  text-decoration: none;
  font-size: 2em;
  padding: 5px 10px;
}

.nav-link:hover {
  text-decoration: underline;
}

.nav-link.active {
  font-weight: bold;
  text-decoration: none;
  pointer-events: none;
}

.nav-link.disabled {
  cursor: not-allowed;
  text-decoration: none;
  pointer-events: none;
}

.nav-separator {
  margin: 0 5px;
}

.medications-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
}

.add-manually-btn {
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 1rem;
}

h2 {
  margin-top: 0;
}

.accordion {
  margin-top: 10px;
}

.accordion-item {
  border-bottom: 1px solid #d3d7db;
}

.accordion-button,
button.accordion-button {
  padding: 15px;
  width: 100%;
  text-align: left;
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 18px;
}

.accordion-content {
  display: none;
  padding: 15px;
}

.accordion-item.active .accordion-content {
  display: block;
}

.form-container {
  padding: 20px;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
}

h2 {
  margin-top: 0;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #d3d7db;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-group input[type="checkbox"],
.form-group input[type="radio"] {
  width: auto;
}

.form-actions {
  display: flex;
  justify-content: space-between;
}

.save-btn,
.cancel-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 48%;
}

@media only screen and (max-width: 576px) {
  .mediationmodal {
    margin-top: 20%;
  }
}
body {
  margin: 0;
  padding: 0;
}
.container {
  margin: 20px auto;
  overflow-x: auto;
}
h2 {
  text-align: center;
}
table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}
th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #d3d7db;
}
.button-container {
  text-align: center;
  margin: 20px 0;
}
.button-container button {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.nav-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0;
}
.nav-links a,
.nav-links span {
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 5px;
  margin: 5px 0;
  width: 80%;
  text-align: center;
}
.nav-links a.active,
#current-link {
  pointer-events: none;
}
.nav-links span.disabled {
  cursor: not-allowed;
  pointer-events: none;
}
.durationDays {
  align-content: end;
  margin-left: 2%;
}

/* Medication link styles */
.med-link {
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
}

.med-link:hover {
  text-decoration: none;
}

.med-link--disabled {
  cursor: not-allowed;
  pointer-events: none;
  text-decoration: none;
}

.med-link-mobile {
  text-decoration: none;
  font-weight: 500;
  display: block;
}

/* Medication Info Modal Styles - Matching AddMedicationForm.vue */
.med-info-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  text-align: center;
}

.med-info-modal {
  width: 80%;
  max-width: 50%;
  border-radius: 8px;
  padding: 2rem;
  max-height: 80%;
  overflow: scroll;
}

.med-info-content {
  margin: 1rem 0;
}

/* Section Title */
.section-title {
  margin: 1rem 0;
  font-size: 1.1rem;
  padding-bottom: 0.5rem;
}

.med-info-section-title {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.75rem;
  text-align: center;
}

/* Form layout - Matching AddMedicationForm.vue */
.new-med-form {
  padding: 0;
}

.form-group {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 500;
  margin-bottom: 0.3rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.4rem 0.6rem;
  border: 1px solid #d3d7db;
  border-radius: 4px;
}

.readonly-input {
  cursor: not-allowed;
}

.readonly-input:focus {
  outline: none;
}

select.readonly-input:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Horizontal row of fields */
.form-row {
  display: flex;
  gap: 1rem;
}

.form-row .form-group {
  flex: 1;
}

.time-input-row {
  margin-bottom: 0.5rem;
}

.time-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d3d7db;
  border-radius: 4px;
  font-size: 1rem;
}

/* Dosage group - Matching AddMedicationForm.vue */
.dosage-group {
  display: flex;
  flex-direction: column;
  max-width: 20%;
}

.dosage-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.dosage-input {
  flex: 0 0 80px;
}

.dosage-select {
  flex: 1;
}

.volume-rate-row {
  display: flex;
  gap: 1rem;
}

.volume-group {
  display: flex;
  flex-direction: column;
}

.volume-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.volume-dropdown {
  width: 70px;
}

.admin-times {
  margin-top: 1rem;
}

/* Bottom action buttons - Matching AddMedicationForm.vue */
.form-actions {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0;
  margin-bottom: 1rem;
}

.btn-cancel {
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.btn-edit {
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-create-order {
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.btn-create-order-mobile {
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.btn-edit-ordered-mobile {
  margin-top: 0.5rem;
  margin-left: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  padding: 2rem;
}

.btn-save {
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

textarea.readonly-input {
  resize: none;
  font-family: inherit;
}

/* Autocomplete styles - Matching AddMedicationForm.vue */
.autocomplete {
  position: relative;
}

.autocomplete-input {
  width: 100%;
}

.autocomplete-input[readonly] {
  cursor: not-allowed;
}

/* Signature Badge Styles - Matching CurrentProc.vue */
.sig-card {
  position: relative;
  display: inline-block;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0 8px 0 4px;
  z-index: 999;
  overflow: visible;
  cursor: pointer;
  vertical-align: middle;
}

.signature-badge {
  position: absolute;
  top: 50%;
  left: 100%;
  transform: translateY(-50%);
  margin-left: 8px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 10px 22px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  pointer-events: auto;
}

.signature-badge::before {
  content: "";
  position: absolute;
  left: -18px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  border-right: 18px solid #cb5f50;
  z-index: -1;
}

.signature-badge .pen {
  flex: 0 0 auto;
  margin-left: 2px;
  opacity: 0.95;
}

.sig-text {
  display: inline-block;
  text-align: left;
}

@media (max-width: 420px) {
  .signature-badge {
    font-size: 11px;
    padding: 8px 12px 8px 18px;
    margin-left: 6px;
  }
  .signature-badge::before {
    left: -14px;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 14px solid #cb5f50;
  }
}

.cda-source-link {
  text-decoration: underline;
}

</style>
